import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";

const TITLE = "Calculatrice du volume de gaz électronique - Apprenez à calculer facilement le volume du gaz";
const DESCRIPTION =
  "Calculatrice du volume de gaz électronique - Apprenez à calculer facilement le volume du gaz. Calculateur de chimie en francais pour cours et TP, avec saisie simple, resultat rapide et interpretation claire.";
const KEYWORDS =
  "gas volume calculator, calculatrice chimie, formule de chimie, tp de chimie, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/gasvolume/";

function GasVolumeCalculator() {
  const [mass, setMass] = useState("");
  const [density, setDensity] = useState("");
  const [volume, setVolume] = useState("");
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState("");

  const handleCalculate = () => {
    if (!mass || !density) {
      setError("Veuillez entrer la masse et la densité correctement");
      setVolume("");
      setExplanation("");
      return;
    }

    const massValue = parseFloat(mass);
    const densityValue = parseFloat(density);

    if (isNaN(massValue) || isNaN(densityValue)) {
      setError("Veuillez entrer des valeurs correctes pour la masse et la densité");
      setVolume("");
      setExplanation("");
      return;
    }

    const gasVolume = massValue / densityValue;
    setVolume(gasVolume.toFixed(2));
    setExplanation(
      `Volume du gaz (litre) = Masse (gramme) / Densité = ${massValue} gramme / ${densityValue} g/cm³ = ${gasVolume.toFixed(
        2
      )} litre`
    );
    setError("");
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Le volume du gaz peut être calculé en utilisant la loi générale des gaz, connue sous le nom de loi de Boyle-Mariotte ou loi des gaz idéaux. Cette loi décrit le comportement des gaz dans certaines conditions et est une équation mathématique. La loi s'exprime généralement sous cette forme: PV = nRT où:
                </p>
                <ul>
                  <li>
                    P est la pression (Pressure) en pascal (Pa) ou autres unités de pression.
                  </li>
                  <li>
                    V est le volume (Volume) en mètre cube (m³) ou litre (L), et doit être cohérent avec l'unité de pression.
                  </li>
                  <li>n est le nombre de moles (Number of moles) du gaz.</li>
                  <li>
                    R est la constante des gaz (Gas Constant) en joule/mole·kelvin (J/(mol·K)).
                  </li>
                  <li>
                    T est la température (Temperature) en kelvin (K), et doit être cohérent avec l'unité de la constante des gaz.
                  </li>
                </ul>
                Pour calculer le volume du gaz, nous pouvons réorganiser l'équation comme suit: <br />
                V = (nRT) / P <br />
                Quand vous avez les valeurs suivantes: <br />
                <ul>
                  <li>n: nombre de moles du gaz.</li>
                  <li>R: le constant des gaz (dépend des unités de mesure utilisées).</li>
                  <li>T: la température requise en kelvin (K).</li>
                  <li>P: la pression en pascal (Pa).</li>
                  <li>
                    Entrez ces valeurs dans l'équation et vous obtiendrez le volume du gaz dans son unité appropriée (mètre cube ou litre), et assurez-vous que les unités de mesure sont cohérentes correctement.
                  </li>
                </ul>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE} </h2>
                </div>
                <hr />
                <div dir="ltr">
                  <div>
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">Masse (gramme)</label>
                      </b>
                      <input
                        type="number"
                        placeholder="80"
                        value={mass}
                        onChange={(e) => setMass(e.target.value)}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                    <br />
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">
                          Densité (g/cm³)
                        </label>
                      </b>
                      <input
                        type="number"
                        placeholder="8"
                        value={density}
                        onChange={(e) => setDensity(e.target.value)}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                    <br />
                    <button
                      className="btn btn-success"
                      style={{ fontSize: "20px" }}
                      onClick={handleCalculate}
                    >
                      Calculer le volume du gaz
                    </button>
                  </div>
                  <br />
                  <div>
                    {error !== "" ? (
                      <p className="p-3 mb-2 bg-warning text-white">
                        Erreur: {error}
                      </p>
                    ) : (
                      <>
                        {volume !== "" && (
                          <>
                            <p className="p-3 mb-2 bg-success text-white">
                              Volume du gaz (litre): {volume} litre
                            </p>
                            {explanation !== "" && (
                              <p className="p-3 mb-2 bg-info text-black">
                                Étapes de calcul: <br />
                                {explanation}
                              </p>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
      {" "}
      <br />
    </div>
  );
}

export default GasVolumeCalculator;
